/**
 * FILL_THE_GAP placeholder rules, shared with the admin editor
 * (sensay-assessment-admin: src/utils/fillTheGapPlaceholders.js).
 *
 * Authors may drop a `__N__` token (1-based, matching the order of the
 * configured gaps) anywhere in the exercise text or question. Wherever a token
 * appears, that gap's answer field is rendered inline at exactly that spot.
 * Gaps whose token appears in neither field keep the previous behavior: they
 * are rendered as a separate labeled field below the passage.
 *
 * The admin editor scans with the exact same rules so what it reports about a
 * definition matches what the candidate actually sees. Keep the two copies in
 * sync.
 */

// Source only — callers build their own RegExp so the shared `lastIndex` of a
// global literal can never leak between scans.
export const GAP_PLACEHOLDER_SOURCE = "__(\\d+)__";

/** The token an author must type to place the gap at `gapIndex` (0-based). */
export function gapPlaceholderToken(gapIndex) {
  return `__${gapIndex + 1}__`;
}

/**
 * Replaces placeholders in HTML text content while leaving tags and attributes
 * untouched. The replacer receives `(token, number, offset)`.
 */
export function replaceGapPlaceholdersInText(text, replacer) {
  const source = String(text ?? "");
  const regex = new RegExp(GAP_PLACEHOLDER_SOURCE, "g");
  let result = "";
  let textStart = 0;

  for (let i = 0; i < source.length; i++) {
    if (source[i] !== "<" || !/[A-Za-z!/?]/.test(source[i + 1] || "")) continue;

    let quote = null;
    let tagEnd = -1;
    if (source.startsWith("<!--", i)) {
      const commentEnd = source.indexOf("-->", i + 4);
      if (commentEnd !== -1) tagEnd = commentEnd + 2;
    } else {
      for (let j = i + 1; j < source.length; j++) {
        const char = source[j];
        if (quote) {
          if (char === quote) quote = null;
        } else if (char === '"' || char === "'") {
          quote = char;
        } else if (char === ">") {
          tagEnd = j;
          break;
        }
      }
    }
    if (tagEnd === -1) continue;

    result += source.slice(textStart, i).replace(
      regex,
      (token, number, offset) => replacer(token, number, textStart + offset),
    );
    result += source.slice(i, tagEnd + 1);
    textStart = tagEnd + 1;
    i = tagEnd;
  }

  return (
    result +
    source
      .slice(textStart)
      .replace(regex, (token, number, offset) =>
        replacer(token, number, textStart + offset),
      )
  );
}

/**
 * Every `__N__` occurrence in one field's HTML text content, in document order.
 * Returns `[{ token, number, gapIndex, start, end }]`, where `gapIndex` is the
 * 0-based gap the token refers to (`number - 1`).
 */
export function findGapPlaceholders(text) {
  const found = [];
  if (text === null || text === undefined) return found;

  const source = String(text);
  replaceGapPlaceholdersInText(source, (token, value, start) => {
    const number = Number.parseInt(value, 10);
    found.push({
      token,
      number,
      gapIndex: number - 1,
      start,
      end: start + token.length,
    });
    return token;
  });
  return found;
}

/**
 * Where every gap's placeholder lives across the given fields.
 *
 * @param fields   `[{ name, value }]` in the order they are rendered — the
 *                 first field containing a token wins for that gap.
 * @param gapCount number of configured gaps.
 *
 * Returns:
 *  - `placements`      `{ [gapIndex]: fieldName }` for gaps rendered inline
 *  - `placedIndexes`   gap indexes rendered inline, ascending
 *  - `fallbackIndexes` gap indexes with no token, rendered as separate fields
 *  - `duplicates`      `[{ number, gapIndex, count }]` for tokens repeated
 *                      across all fields — only the first becomes a field
 *  - `unknown`         `[{ number, token, field }]` tokens matching no gap;
 *                      these stay visible as plain text so authors spot them
 */
export function analyzeGapPlaceholders(fields = [], gapCount = 0) {
  const placements = {};
  const counts = new Map();
  const unknown = [];

  (fields || []).forEach((field) => {
    const name = field?.name;
    findGapPlaceholders(field?.value).forEach((placeholder) => {
      const { number, gapIndex, token } = placeholder;
      if (gapIndex < 0 || gapIndex >= gapCount) {
        unknown.push({ number, token, field: name });
        return;
      }
      counts.set(number, (counts.get(number) || 0) + 1);
      if (placements[gapIndex] === undefined) {
        placements[gapIndex] = name;
      }
    });
  });

  const placedIndexes = [];
  const fallbackIndexes = [];
  for (let i = 0; i < gapCount; i++) {
    if (placements[i] === undefined) fallbackIndexes.push(i);
    else placedIndexes.push(i);
  }

  const duplicates = [];
  counts.forEach((count, number) => {
    if (count > 1) duplicates.push({ number, gapIndex: number - 1, count });
  });
  duplicates.sort((a, b) => a.number - b.number);

  return { placements, placedIndexes, fallbackIndexes, duplicates, unknown };
}
